import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL } from "../../config/constants";
import AppSidebar from "../../components/AppSidebar";
import TopBar from "../../components/TopBar";
import ActivityTaxonomy, { formatLevels } from "../../components/activityTaxonomy";
import activityTypeIcons from "../../assets/activity_types/icons";
import ActivityDetailModal from "../../components/activityDetailModal";
import EyeIcon from "../../assets/eye.png";
import {
  Layout, Main, Content,
  TitleRow, PageTitle, TabsRow, Tab,
  ActivityPanel, PanelHeader, TypeIconWrap, TypeInfo, TypeName, TypeSubtitle, SelectedCounter,
  FilterRow, FilterLabel, FilterField, FilterFieldLabel, FilterSelect,
  ActivitiesGrid, ActivityCard, CardTop, Checkbox, CardName, SeenBadge, EditIconBtn,
  GoalLabel, GoalText, LoadingMsg,
} from "./styles";

/* ── Icons ── */
const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const ALL = "";

// Orden de los niveles: I, II, III, IV, V. Un codigo que no sea romano va al final.
const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
const romanValue = (code) => {
  const i = ROMAN.indexOf(code);
  return i === -1 ? ROMAN.length : i;
};

// Opciones unicas de un campo, en el orden en que aparecen las actividades, que
// vienen ordenadas por codigo clinico.
const optionsOf = (activities, idField, labelField) => {
  const seen = new Map();
  for (const act of activities) {
    if (act[idField] != null && act[labelField] && !seen.has(act[idField])) {
      seen.set(act[idField], act[labelField]);
    }
  }
  return [...seen].map(([value, label]) => ({ value: String(value), label }));
};

/* ── Component ── */
const TherapistActivityPlan = () => {
  const { id }      = useParams();
  const location    = useLocation();
  const { t, i18n } = useTranslation();
  const token       = localStorage.getItem("token");

  const [patient]                         = useState(location.state?.patient || null);
  const [activeTypeId, setActiveTypeId]   = useState(1);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activities, setActivities]       = useState([]);
  const [prescribed, setPrescribed]       = useState(new Set());
  const [viewed, setViewed]               = useState(new Set());
  const [loading, setLoading]             = useState(true);
  const [openActivity, setOpenActivity]   = useState(null);

  const [dimensionId, setDimensionId]       = useState(ALL);
  const [subDimensionId, setSubDimensionId] = useState(ALL);
  const [levelValue, setLevelValue]         = useState(ALL);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [typesRes, actRes, presRes] = await Promise.all([
          fetch(`${API_URL}/activities/types`, { headers }),
          fetch(`${API_URL}/activities?lang=${i18n.language}`, { headers }),
          fetch(`${API_URL}/patients/${id}/prescriptions`, { headers }),
        ]);
        const typesData = await typesRes.json();
        const actData   = await actRes.json();
        const presData  = await presRes.json();
        setActivityTypes(Array.isArray(typesData) ? typesData : []);
        setActivities(Array.isArray(actData)   ? actData   : []);
        const presList = Array.isArray(presData) ? presData : [];
        setPrescribed(new Set(presList.map((p) => p.activity_id)));
        setViewed(new Set(presList.filter((p) => p.seen_this_week).map((p) => p.activity_id)));
      } catch (err) {
        console.error("TherapistActivityPlan fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id, i18n.language]); // eslint-disable-line

  // Lo usan el checkbox de la tarjeta y el de la ficha: el estado es uno solo,
  // así que marcar en la ficha ya se ve en la rejilla al cerrarla.
  const togglePrescription = async (activityId) => {
    const next = new Set(prescribed);
    if (next.has(activityId)) next.delete(activityId);
    else next.add(activityId);
    setPrescribed(next);

    // Una prescripción nueva empieza sin aperturas, así que cualquier toggle
    // deja la actividad como no vista.
    const nextViewed = new Set(viewed);
    nextViewed.delete(activityId);
    setViewed(nextViewed);

    try {
      const res = await fetch(`${API_URL}/patients/${id}/prescriptions/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activity_id: activityId }),
      });
      // fetch no rechaza con un 4xx/5xx: sin esto, un error del servidor dejaba
      // la casilla marcada sin que la prescripción existiera.
      if (!res.ok) throw new Error(res.status);
    } catch (err) {
      setPrescribed(prescribed);
      setViewed(viewed);
      console.error("toggle error:", err);
    }
  };

  const typeActivities = useMemo(
    () => activities.filter((a) => a.activity_type_id === activeTypeId),
    [activities, activeTypeId]
  );

  // Las opciones salen de las actividades del tipo activo: así no se puede
  // elegir una combinación que deje la rejilla vacía por construcción.
  const dimensionOptions = useMemo(
    () => optionsOf(typeActivities, "dimension_id", "dimension"),
    [typeActivities]
  );
  const subDimensionOptions = useMemo(
    () => optionsOf(
      dimensionId === ALL
        ? typeActivities
        : typeActivities.filter((a) => String(a.dimension_id) === dimensionId),
      "sub_dimension_id",
      "sub_dimension"
    ),
    [typeActivities, dimensionId]
  );
  // El nivel se filtra de uno en uno: una actividad "VFCS III - V" sale al
  // filtrar por III, por IV y por V. El valor es "VFCS|III" por si algun dia
  // conviven varias escalas.
  const levelOptions = useMemo(() => {
    const seen = new Map();
    for (const act of typeActivities) {
      for (const code of act.difficulty_levels) {
        const value = `${act.difficulty_scale}|${code}`;
        if (!seen.has(value)) seen.set(value, { value, scale: act.difficulty_scale, code });
      }
    }
    return [...seen.values()].sort((a, b) =>
      (a.scale || "").localeCompare(b.scale || "") || romanValue(a.code) - romanValue(b.code));
  }, [typeActivities]);

  const visible = useMemo(
    () => typeActivities.filter((a) => {
      if (dimensionId !== ALL && String(a.dimension_id) !== dimensionId) return false;
      if (subDimensionId !== ALL && String(a.sub_dimension_id) !== subDimensionId) return false;
      if (levelValue !== ALL && !a.difficulty_levels.some((code) => `${a.difficulty_scale}|${code}` === levelValue)) return false;
      return true;
    }),
    [typeActivities, dimensionId, subDimensionId, levelValue]
  );

  // El contador mira la categoría entera, no lo filtrado: es cuántas tiene
  // prescritas el paciente en Motor, no cuántas se ven ahora mismo.
  const selectedCount = typeActivities.filter((a) => prescribed.has(a.id)).length;
  const activeType    = activityTypes.find((type) => type.id === activeTypeId) || {};

  const selectType = (typeId) => {
    setActiveTypeId(typeId);
    // Los filtros son de la categoría: sus opciones no existen en la siguiente.
    setDimensionId(ALL);
    setSubDimensionId(ALL);
    setLevelValue(ALL);
  };

  const selectDimension = (value) => {
    setDimensionId(value);
    // La sub-dimensión elegida puede no colgar de la dimensión nueva.
    setSubDimensionId(ALL);
  };

  return (
    <Layout>
      <TopBar />
      <AppSidebar patient={patient} activeNav="activityPlan" />

      <Main>
        <Content>
          <TitleRow>
            <PageTitle>{t("activityPlan.title")}</PageTitle>
            <TabsRow>
              {activityTypes.map((type) => (
                <Tab key={type.id} $active={type.id === activeTypeId} onClick={() => selectType(type.id)}>
                  {t(`activityTypes.${type.id}.name`, { defaultValue: type.name })}
                </Tab>
              ))}
            </TabsRow>
          </TitleRow>

          <ActivityPanel>
            <PanelHeader>
              <TypeIconWrap src={activityTypeIcons[activeTypeId]} alt="" />
              <TypeInfo>
                <TypeName>{t(`activityTypes.${activeType.id}.name`, { defaultValue: activeType.name })}</TypeName>
                <TypeSubtitle>{t(`activityTypes.${activeType.id}.description`, { defaultValue: activeType.description })}</TypeSubtitle>
              </TypeInfo>
              <SelectedCounter>
                {t("activityPlan.selectedCount", { selected: selectedCount, total: typeActivities.length })}
              </SelectedCounter>
            </PanelHeader>

            <FilterRow>
              <FilterLabel>{t("activityPlan.filterBy")}</FilterLabel>

              <FilterField>
                <FilterFieldLabel>{t("activityPlan.dimension")}</FilterFieldLabel>
                <FilterSelect value={dimensionId} onChange={(e) => selectDimension(e.target.value)}>
                  <option value={ALL}>{t("activityPlan.all")}</option>
                  {dimensionOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </FilterSelect>
              </FilterField>

              <FilterField>
                <FilterFieldLabel>{t("activityPlan.subDimension")}</FilterFieldLabel>
                <FilterSelect value={subDimensionId} onChange={(e) => setSubDimensionId(e.target.value)}>
                  <option value={ALL}>{t("activityPlan.all")}</option>
                  {subDimensionOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </FilterSelect>
              </FilterField>

              <FilterField>
                <FilterFieldLabel>{t("activityPlan.vfcsLevels")}</FilterFieldLabel>
                <FilterSelect value={levelValue} onChange={(e) => setLevelValue(e.target.value)}>
                  <option value={ALL}>{t("activityPlan.all")}</option>
                  {levelOptions.map((o) => (
                    <option key={o.value} value={o.value}>{formatLevels(o.scale, [o.code])}</option>
                  ))}
                </FilterSelect>
              </FilterField>
            </FilterRow>

            {loading ? (
              <LoadingMsg>{t("activityPlan.loading")}</LoadingMsg>
            ) : visible.length === 0 ? (
              <LoadingMsg>
                {typeActivities.length === 0
                  ? t("activityPlan.emptyCategory")
                  : t("activityPlan.noMatch")}
              </LoadingMsg>
            ) : (
              <ActivitiesGrid>
                {visible.map((act) => {
                  const isSelected = prescribed.has(act.id);
                  return (
                    <ActivityCard key={act.id} $selected={isSelected} onClick={() => setOpenActivity(act)}>
                      <CardTop>
                        {/* Solo el checkbox prescribe: el resto de la tarjeta abre
                            la ficha, para no prescribir sin querer al ir a leerla. */}
                        <Checkbox
                          $checked={isSelected}
                          role="checkbox"
                          aria-checked={isSelected}
                          aria-label={t("activityCard.addToPlan")}
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePrescription(act.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              togglePrescription(act.id);
                            }
                          }}
                        >
                          {isSelected && <CheckIcon />}
                        </Checkbox>
                        <CardName>{act.name}</CardName>
                        {viewed.has(act.id) && (
                          <SeenBadge>
                            <img src={EyeIcon} alt="" />
                            {t("activityPlan.seen")}
                          </SeenBadge>
                        )}
                        <EditIconBtn
                          type="button"
                          aria-label={act.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenActivity(act);
                          }}
                        >
                          <PencilIcon />
                        </EditIconBtn>
                      </CardTop>

                      <ActivityTaxonomy activity={act} />

                      {act.specific_goal && (
                        <>
                          <GoalLabel>{t("activityPlan.specificGoal")}</GoalLabel>
                          <GoalText>{act.specific_goal}</GoalText>
                        </>
                      )}
                    </ActivityCard>
                  );
                })}
              </ActivitiesGrid>
            )}
          </ActivityPanel>
        </Content>
      </Main>

      {openActivity && (
        <ActivityDetailModal
          activity={openActivity}
          onClose={() => setOpenActivity(null)}
          canPrescribe
          patientId={Number(id)}
          prescribed={prescribed.has(openActivity.id)}
          onTogglePrescribed={() => togglePrescription(openActivity.id)}
        />
      )}
    </Layout>
  );
};

export default TherapistActivityPlan;
